import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  DialogActions,
  TextField,
  Typography
} from '@mui/material';

import { Dialog } from 'components/shared';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { t } from 'i18next';
import {
  ExpenseClaimResponse,
  TravelMovementPerDayResponse
} from 'generated/index';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

interface Props {
  open: boolean;
  title?: string;
  messages?: {
    title: string;
    messages: string[];
  }[];
  onSubmit: (data: { remark: string }) => void;
  onCancel: () => void;
  selectedRows: (ExpenseClaimResponse | TravelMovementPerDayResponse)[];
}

const schema = Yup.object().shape({
  remark: Yup.string()
    .required(t('travelExpenseClaims.reasonRequired'))
    .min(5, t('travelExpenseClaims.reasonToShort'))
});

export default function WarningDialogAdvanced({
  messages = [],
  onSubmit,
  onCancel,
  open,
  title,
  selectedRows
}: Props) {
  const { t } = useTranslation('common');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { remark: '' }
  });

  const columns: GridColDef[] = [
    {
      field: 'employeeName',
      headerName: t('employee'),
      flex: 1,
      valueGetter: (
        _,
        row: ExpenseClaimResponse | TravelMovementPerDayResponse
      ) => row.employment.employeeName
    },
    {
      field: 'date',
      headerName: t('date'),
      width: 100
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 2
    }
  ];

  const handleFormSubmit = (data: { remark: string }) => {
    onSubmit(data);
    reset({ remark: '' });
  };

  return (
    <Dialog
      open={open}
      title={title || t('activityPlanning.warning')}
      handleClose={onCancel}
      sx={{
        '& .MuiDialog-paper.MuiDialog-paperScrollPaper': {
          width: 480
        }
      }}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box display="flex" flexDirection="column">
          {messages.map(({ title, messages }, i: number) => {
            return (
              <Box
                key={i}
                marginTop={messages.length > 1 && i !== 0 ? '20px' : 'initial'}
              >
                <Typography variant="body1">{title}</Typography>
                {messages.map((message: string, j: number) => (
                  <Typography key={j} variant="body2">
                    • {message}
                  </Typography>
                ))}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            marginTop: messages.length > 1 ? '20px' : 'initial'
          }}
        >
          <Typography variant="body1">
            {selectedRows.length > 1
              ? t('rejectionMultipleSelected')
              : t('rejectionSingleSelected')}
          </Typography>
          <Box
            sx={{
              marginBottom: (theme) => theme.spacing(3.5),
              marginTop: (theme) => theme.spacing(2)
            }}
          >
            <TableCRUDLayout
              rows={selectedRows}
              columns={columns}
              editable={false}
              autoHeight
              disableRowSelectionOnClick
              hideFooter
              disableActions={true}
            />
          </Box>
          <Typography variant="body1">
            {t('rejectionReasonDescription')}
          </Typography>
          <Controller
            name="remark"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('remark')}
                multiline
                rows={4}
                error={!!errors.remark}
                helperText={errors.remark?.message}
              />
            )}
          />
        </Box>

        <DialogActions className="dialog-actions">
          <Button onClick={onCancel} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <Button type="submit" color="primary" variant="contained">
            {t('travelExpenseClaims.actions.REJECT')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
